import { useMemo, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { Seo } from '@saas-pages/shared/public/seo'
import { useLocale, useT } from '@/shared/i18n'
import { seoFor } from '@/seo/routes'
import {
  buildUtilities,
  type UtilityCard,
  type UtilityTaskCategory,
} from '@/shared/utilities/utilities'

const HOME_CATEGORY_ORDER = [
  'compression',
  'pdf-editing',
  'pdf-conversion',
  'image-pdf',
  'security',
] as const satisfies readonly UtilityTaskCategory[]

function normalize(text: string): string {
  return text.toLowerCase().replaceAll('ё', 'е')
}

function matchesQuery(tool: UtilityCard, query: string): boolean {
  const haystack = normalize(`${tool.title} ${tool.shortDescription} ${tool.longDescription}`)
  return normalize(query)
    .split(/\s+/)
    .filter(Boolean)
    .every((word) => haystack.includes(word))
}

export function IndexPage() {
  const t = useT()
  const { locale } = useLocale()
  const [query, setQuery] = useState('')
  const utilities = useMemo(() => buildUtilities(t), [t])
  const visible = utilities.filter((utility) => matchesQuery(utility, query))
  const searching = query.trim() !== ''

  const groupedUtilities = HOME_CATEGORY_ORDER.map((category) => ({
    category,
    ...t.hero.categoryLabels[category],
    tools: visible.filter((utility) => utility.taskCategory === category),
  })).filter((group) => group.tools.length > 0)

  return (
    <>
      <Seo {...seoFor('index', locale)} />

      <section className="mx-auto max-w-3xl">
        <h1 className="text-2xl font-bold tracking-tight sm:text-4xl">{t.hero.title}</h1>
        <p className="mt-2 text-sm text-muted-foreground sm:text-base">{t.hero.sub}</p>

        <div className="mt-4">
          <label htmlFor="tool-search" className="sr-only">
            {t.hero.searchLabel}
          </label>
          <input
            id="tool-search"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t.hero.searchPlaceholder}
            autoComplete="off"
            className="h-12 w-full min-w-0 rounded-lg border border-input bg-background px-4 text-base text-foreground shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>

        {!searching && (
          <nav aria-label={t.hero.toolsLabel} className="mt-3 flex flex-wrap gap-2">
            {groupedUtilities.map((group) => (
              <a
                key={group.category}
                href={`#${group.category}`}
                className="rounded-md border bg-background px-3 py-1.5 text-sm text-foreground transition-colors hover:border-primary/50 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {group.title}
              </a>
            ))}
          </nav>
        )}
      </section>

      <section id="tools" aria-label={t.hero.toolsLabel} className="mx-auto mt-6 max-w-5xl space-y-8">
        {groupedUtilities.length === 0 && (
          <div role="status" className="rounded-lg border bg-card p-4 text-sm text-muted-foreground">
            <p>{t.hero.noResults}</p>
            <button
              type="button"
              onClick={() => setQuery('')}
              className="mt-2 font-medium text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {t.hero.clearSearch}
            </button>
          </div>
        )}

        {groupedUtilities.map((group) => (
          <section key={group.category} id={group.category} className="scroll-mt-24">
            <h2 className="text-lg font-semibold text-foreground">{group.title}</h2>
            <p className="mt-0.5 text-sm text-muted-foreground">{group.description}</p>
            <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-3 lg:grid-cols-3">
              {group.tools.map((tool) => (
                <UtilityCardItem key={tool.key} tool={tool} />
              ))}
            </div>
          </section>
        ))}
      </section>

      <section
        aria-label={t.hero.aboutLabel}
        className="prose prose-slate mx-auto mt-16 max-w-3xl text-base text-muted-foreground"
      >
        <h2 className="text-2xl font-semibold tracking-tight text-foreground">
          {t.hero.aboutTitle}
        </h2>
        <p className="mt-4">
          {t.hero.aboutPara1Prefix}
          <Link to="/povernut" className="text-primary underline-offset-2 hover:underline">
            {t.hero.aboutPara1Tool1}
          </Link>
          {t.hero.aboutPara1Mid}
          <Link to="/v-jpg" className="text-primary underline-offset-2 hover:underline">
            {t.hero.aboutPara1Tool2}
          </Link>
          {t.hero.aboutPara1Suffix}
        </p>
        <p className="mt-4">{t.hero.aboutPara3}</p>
      </section>

      <aside
        data-rsya-slot="footer-banner"
        aria-hidden="true"
        className="mx-auto mt-12 min-h-[120px] max-w-3xl"
      />
    </>
  )
}

function UtilityCardItem({ tool }: { tool: UtilityCard }) {
  return (
    <Link
      to={tool.path}
      className="group flex min-w-0 items-start gap-3 rounded-lg border bg-card p-3 shadow-sm transition-colors hover:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <span
        aria-hidden
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted text-xs font-semibold text-muted-foreground"
      >
        {tool.icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-medium text-foreground group-hover:text-primary">
          {tool.title}
        </span>
        <span className="mt-0.5 line-clamp-2 block text-sm text-muted-foreground">
          {tool.shortDescription}
        </span>
      </span>
    </Link>
  )
}
